import { isAxiosError } from 'axios';
import { generateDynamicPath } from 'common/utils';
import { communityActions, selectMyCommunitiesIds } from 'community/api';
import { EventStatus } from 'domain/event';
import i18n from 'i18n';
import { over } from 'lodash-es';
import { addAppConfirmationDialog, addAppErrorMessage, addAppSuccessMessage } from 'modules/alerts/state';
import { getErrorMessagesFromResponse } from 'modules/api/utils';
import { AppRoutes } from 'modules/router';
import { selectRouteByPathname } from 'modules/router/state';
import { push } from 'redux-first-history';
import type { Effect } from 'store';
import { handleQueryError } from 'store/utils';

import { eventApi } from './eventApi';
import { selectEventCommunityId } from './selectors';
import { DeleteEventArgs, DeleteEventsFromSeriesArgs, RegisterForEventArgs } from './types';

export const deleteEventSuccessEffect: Effect = ({ meta }, { dispatch, getState }) => {
  const { silentDelete } = meta.arg.originalArgs as DeleteEventArgs;

  const state = getState();
  const route = selectRouteByPathname(state);
  const isEventDetailsPage = route?.path === AppRoutes.EventDetails;

  if (!isEventDetailsPage) return;

  dispatch(push(AppRoutes.MyEvents));

  if (silentDelete) return;

  dispatch(
    addAppConfirmationDialog({
      title: i18n.t('event:deleteEvent.successTitle'),
      description: i18n.t('event:deleteEvent.successDescriptionSingleEvent'),
    })
  );
};

export const deleteEventsFromSeriesSuccessEffect: Effect = ({ meta }, { dispatch }) => {
  const { eventSeries, eventIdsToRemove } = meta.arg.originalArgs as DeleteEventsFromSeriesArgs;

  const eventsToRemove = eventSeries.events.filter(({ id }) => eventIdsToRemove.includes(id));
  const description =
    eventsToRemove.length === 1
      ? i18n.t('event:deleteEvent.successDescriptionEventSeriesSingle', { eventName: eventsToRemove[0].name })
      : i18n.t('event:deleteEvent.successDescriptionEventSeriesMultiple', { count: eventsToRemove.length });

  dispatch(
    addAppConfirmationDialog({
      title: i18n.t('event:deleteEvent.successTitle'),
      description,
    })
  );
};

export const startEventFailedEffect: Effect = ({ payload: error }, { dispatch }) => {
  handleQueryError(error, dispatch, 'eventApi.startMeeting', 'Unable to start meeting');
};

export const startEventSuccessEffect: Effect = ({ meta }, { dispatch }) => {
  const { id } = meta.arg.originalArgs;

  // https://natterco.atlassian.net/browse/N2-1532
  dispatch(
    eventApi.util.updateQueryData('getEventDetails', { id }, (data) => {
      data.status = EventStatus.Started;
    })
  );

  dispatch(push(generateDynamicPath(AppRoutes.JoinEvent, { id }), { allow: true }));
};

export const getConnectionsFailedEffect: Effect = ({ payload: error }, { dispatch }) => {
  handleQueryError(error, dispatch, 'eventApi.getConnections', 'Unable to fetch user connections');
};

export const stopEventFailedEffect: Effect = ({ payload: error }, { dispatch }) => {
  if (isAxiosError(error)) {
    getErrorMessagesFromResponse(error.response).forEach((errorMessage) => {
      dispatch(
        addAppErrorMessage({
          metadata: 'MeetingService.stopMeeting',
          message: 'Unable to stop meeting',
          error: new Error(errorMessage),
        })
      );
    });
  }
};

const updateEventDetails: Effect = ({ meta }, { dispatch }) => {
  const { id } = meta.arg.originalArgs;
  dispatch(
    eventApi.util.updateQueryData('getEventDetails', { id }, (data) => {
      data.isUserRegistered = true;
    })
  );
};

const updateMyCommunities: Effect = ({ meta }, { dispatch, getState }) => {
  const state = getState();
  const args = meta.arg.originalArgs as RegisterForEventArgs;

  const myCommunitiesIds = selectMyCommunitiesIds(state);
  // Prefer communityId from args (for magic link flow), fall back to selector (for event details page flow).
  // Note: In magic link flow, communityId MUST be passed in args since the event details page wasn't visited
  // and selectEventCommunityId will return undefined. See useAutoRegisterToEvent for the caller.
  const communityId = args.communityId ?? selectEventCommunityId(state);

  if (communityId !== undefined && !myCommunitiesIds?.includes(communityId)) {
    // user was not in event community before, but registering for event means he was added to the community
    // so we want to refresh community related data
    dispatch(
      communityActions.community.registeredToCommunity({
        communityId,
        // When silent flag is set (e.g., magic link flow), suppress MemberSetup dialog
        showMemberSetupDialog: !args.silent,
      })
    );
  }
};

const addRegisterSuccessMessage: Effect = ({ meta }, { dispatch, getState }) => {
  const state = getState();
  const args = meta.arg.originalArgs as RegisterForEventArgs;

  // Skip success message when silent flag is set (e.g., magic link flow)
  if (args.silent) return;

  const myCommunitiesIds = selectMyCommunitiesIds(state);
  // Prefer communityId from args (for magic link flow), fall back to selector (for event details page flow)
  const communityId = args.communityId ?? selectEventCommunityId(state);

  if (communityId && myCommunitiesIds?.includes(communityId)) {
    dispatch(addAppSuccessMessage('You registered successfully to Event'));
  }
};

export const registerForEventSuccessEffect: Effect = ({ meta }, { dispatch, getState }) => {
  over(updateEventDetails, updateMyCommunities, addRegisterSuccessMessage)({ meta }, { dispatch, getState });
};

export const registerForEventFailedEffect: Effect = ({ meta, payload: error }, { dispatch }) => {
  const { id } = meta.arg.originalArgs;

  dispatch(
    eventApi.util.updateQueryData('getEventDetails', { id }, (data) => {
      data.isUserRegistered = false;
    })
  );
  handleQueryError(error, dispatch, 'eventApi.registerForEvent', 'Unable to register to Event');
};

export const refreshEventsList: Effect = (_args, { dispatch }) => {
  dispatch(eventApi.util.invalidateTags(['ALL_EVENTS', 'UPCOMING_EVENT', 'EVENT']));
};
